import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { Collapse, FormControlLabel, Radio, RadioGroup } from "@mui/material";
import AdvanceSlicesFormBody from "components/AdvanceManagement/AdvanceSlicesFormBody";
import type { TAdvancePayment } from "components/AdvanceManagement/NewAdvanceModal";
import LightButton from "components/shared/LightButton";
import MessageBlock from "components/shared/MessageBlock";
import { format, isValid } from "date-fns";
import { storeAdvancePaymentNavigationTarget } from "helpers/advancePaymentNavigation";
import { getCleanStartorEndDay, isObjectEmpty } from "helpers/utils";
import type { Dispatch, SetStateAction } from "react";
import { useEffect, useRef, useState } from "react";
import { useFieldArray, useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import type { IAdvanceSlice } from "store/advancePaymentSlice";
import { PaymentStatus } from "store/types";
import { v4 as uuidv4 } from "uuid";

import { clampAdvanceSlicePaymentDate, getDefaultAdvanceSlicePaymentDate } from "../../utils";

export interface IInlineAdvanceForm {
    /**
     * Called with transformed slice data (amounts in cents) on every slice change.
     * Passes `null` when no slice has an amount.
     */
    setAdvancePayment: Dispatch<SetStateAction<TAdvancePayment | null>>;
    /**
     * null  = "No" selected (no advance, no error)
     * false = "Yes" selected but at least one amount is missing → disables Next button
     * true  = "Yes" selected and all amounts filled → OK
     */
    setCreateAdvanceInManagement: Dispatch<SetStateAction<boolean | null>>;
    /**
     * Pre-filled slices for edit mode (fetched by parent, amounts in euros)
     * or restored slices after back-navigation in create mode (also in euros).
     */
    initialSlices?: TAdvancePayment["slices"];
    /**
     * Whether the parent form is in create or edit mode.
     */
    mode: "new" | "edit";
    /**
     * Linked advance payment — used to display a deletion warning in edit mode.
     */
    linkedAdvancePayment: IAdvanceSlice | undefined;
    /**
     * Counter incremented by the parent each time the user clicks "Suivant"
     * while this form is open but has incomplete slices.
     * Triggers RHF validation to show field-level errors immediately.
     */
    validateSignal?: number;
    /**
     * EAN13 of the title — used in edit mode to pre-filter the Advances page on navigation.
     */
    titleEan13?: string;
    /**
     * Earliest selectable payment date when the title was not published before the contract period.
     */
    minPaymentDate?: Date;
}

const buildEmptySlice = (minPaymentDate?: Date) => ({
    amount: "",
    paymentDate: getDefaultAdvanceSlicePaymentDate(minPaymentDate),
    status: PaymentStatus.PAID,
    comment: "",
    customStatusId: "",
    key: uuidv4(),
});

/**
 * Inline advance payment slices form embedded in the term form.
 *
 * Uses the same rendering component (AdvanceSlicesFormBody) as NewAdvanceModal,
 * but without a modal wrapper or its own submit button — submission is handled
 * by the parent form.
 *
 * Owns its own react-hook-form state and propagates transformed data
 * via `setAdvancePayment` / `setCreateAdvanceInManagement` callbacks.
 */
const InlineAdvanceForm = ({
    setAdvancePayment,
    setCreateAdvanceInManagement,
    initialSlices,
    mode,
    linkedAdvancePayment,
    validateSignal,
    titleEan13,
    minPaymentDate,
}: IInlineAdvanceForm) => {
    const [t] = useTranslation();
    const navigate = useNavigate();

    const handleGoToAdvancePayment = () => {
        const paymentId = linkedAdvancePayment?.paymentId ?? linkedAdvancePayment?.id ?? null;
        storeAdvancePaymentNavigationTarget({ titleEan13, paymentId });
        navigate("/advance-payment");
    };

    // Show the slices form if initial slices exist (edit mode with linked advance, or create mode after back-navigation).
    const [showForm, setShowForm] = useState<boolean>(!!initialSlices?.length);

    const {
        control,
        watch,
        formState: { errors },
        setValue,
        reset,
        trigger,
        getValues,
    } = useForm<TAdvancePayment>({
        mode: "onTouched",
        defaultValues: {
            term: null,
            slices: [buildEmptySlice(minPaymentDate)],
            isForfait: 0,
            forceOver4Pass: 0,
        },
    });

    const { fields, append, remove } = useFieldArray({ name: "slices", control });
    const watchedSlices = useWatch({ control, name: "slices" });

    // Ensures form restoration from parent state only happens once per mount.
    // In new mode with no pre-loaded slices, mark as initialized immediately so
    // that user keystrokes (which feed back into initialSlices via the parent's
    // advancePayment state) never trigger a reset and steal the input focus.
    const hasInitialized = useRef(mode === "new" && !initialSlices?.length);

    useEffect(() => {
        if (!hasInitialized.current && initialSlices?.length) {
            reset({ term: null, slices: initialSlices, isForfait: 0, forceOver4Pass: 0 });
            setShowForm(true);
            hasInitialized.current = true;
        }
    }, [initialSlices?.length]);

    useEffect(() => {
        if (!showForm || !minPaymentDate) return;

        const slices = getValues("slices") ?? [];
        slices.forEach((slice, index) => {
            const clampedDate = clampAdvanceSlicePaymentDate(slice.paymentDate, minPaymentDate);
            const currentDate = slice.paymentDate
                ? getCleanStartorEndDay(slice.paymentDate as Date, { time: "start" })
                : null;

            if (!currentDate || currentDate.getTime() !== clampedDate.getTime()) {
                setValue(`slices.${index}.paymentDate`, clampedDate, { shouldValidate: true });
            }
        });
    }, [minPaymentDate, showForm, getValues, setValue]);

    // Syncs slice data to parent state on every change.
    // Amounts are converted from euros (user input) to cents (API format).
    useEffect(() => {
        if (!showForm) return;

        const isAmountFilled = (s: (typeof watchedSlices)[0]) => s.amount !== "" && s.amount !== 0 && s.amount !== "0";

        const hasAnyFilledSlice = watchedSlices.some(isAmountFilled);
        // All slices must have an amount before the Next button is enabled.
        const allSlicesFilled = watchedSlices.length > 0 && watchedSlices.every(isAmountFilled);

        setCreateAdvanceInManagement(allSlicesFilled ? true : false);

        if (!hasAnyFilledSlice) {
            setAdvancePayment(null);
            return;
        }

        const transformedSlices = watchedSlices.map((slice, idx) => ({
            ...slice,
            amount: Math.round(+(slice.amount ?? 0) * 100),
            paymentDate:
                slice.paymentDate && isValid(new Date(slice.paymentDate as Date))
                    ? format(getCleanStartorEndDay(slice.paymentDate as Date), "yyyy-MM-dd")
                    : null,
            sliceRow: idx,
        }));

        setAdvancePayment({
            term: null,
            slices: transformedSlices,
            isForfait: 0,
            forceOver4Pass: 0,
        });
    }, [watchedSlices, showForm]);

    // When validateSignal is incremented by the parent (user clicked "Suivant" with
    // incomplete slices), trigger internal RHF validation to reveal field errors.
    useEffect(() => {
        if (validateSignal && validateSignal > 0 && showForm) {
            void trigger("slices");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [validateSignal, showForm, trigger]);

    // When toggling to "No": reset the internal form and clear parent state.
    // null signals "No selected" (distinct from false = "Yes but empty").
    const handleToggle = (wantsAdvance: boolean) => {
        setShowForm(wantsAdvance);
        if (!wantsAdvance) {
            reset({ term: null, slices: [buildEmptySlice(minPaymentDate)], isForfait: 0, forceOver4Pass: 0 });
            setAdvancePayment(null);
            setCreateAdvanceInManagement(null);
        }
    };

    // Warning: the user is about to delete an existing linked advance payment
    // (edit mode, linked payment exists, but form is hidden or all amounts are empty).
    const isLinkedPaymentBeingRemoved =
        mode === "edit" &&
        !isObjectEmpty(linkedAdvancePayment) &&
        (!showForm || !watchedSlices.some((s) => s.amount !== "" && s.amount !== 0 && s.amount !== "0"));

    return (
        <div className="advance-inline-section">
            <div
                className="form-component"
                style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}
            >
                <label className="form-component-label">{t("ConnectTitle.AdvanceSection.WantToRegister")}</label>
                <RadioGroup
                    row
                    value={showForm ? "1" : "0"}
                    onChange={(e) => mode !== "edit" && handleToggle(e.target.value === "1")}
                    sx={{
                        paddingLeft: "4px",
                        opacity: mode === "edit" ? 0.5 : 1,
                        pointerEvents: mode === "edit" ? "none" : "auto",
                    }}
                >
                    <FormControlLabel
                        value="0"
                        control={<Radio checkedIcon={<CheckCircleIcon width="15px" />} disabled={mode === "edit"} />}
                        label={t("ModalBase.No")}
                    />
                    <FormControlLabel
                        value="1"
                        control={<Radio checkedIcon={<CheckCircleIcon width="15px" />} disabled={mode === "edit"} />}
                        label={t("ModalBase.Yes")}
                    />
                </RadioGroup>
            </div>

            <Collapse in={showForm} unmountOnExit sx={{ marginTop: 0 }}>
                <AdvanceSlicesFormBody
                    formData={{ control, watch, errors, setValue, trigger }}
                    fields={fields}
                    watchedSlices={watchedSlices}
                    appendRow={() => append(buildEmptySlice(minPaymentDate))}
                    readOnly={mode === "edit"}
                    removeRow={remove}
                    isUpdate={mode === "edit" && !!linkedAdvancePayment}
                    isForfait={0}
                    type="advance"
                    compact={mode === "edit"}
                    minPaymentDate={minPaymentDate}
                />
            </Collapse>

            {isLinkedPaymentBeingRemoved && (
                <MessageBlock type="warning" text={t("ConnectTitle.AdvanceSection.WarningDelete")} />
            )}

            {mode === "edit" && showForm && (
                <div className="advance-inline-info">
                    <span>{t("TermRecap.EditAdvancePaymentInfoPrefix")}</span>
                    <LightButton
                        text={t("NavigationBar.AdvancePayment")}
                        onClick={handleGoToAdvancePayment}
                        className="advance-inline-info__link"
                    />
                </div>
            )}
        </div>
    );
};

export default InlineAdvanceForm;
